import { useCallback, useEffect, useState } from 'react'
import Modal from 'react-modal'
import { AppStoreReviewDto } from '@ivy-monorepo/shared'
import dayjs from 'dayjs'
import { adminService } from '../services/admin.service'

interface AppStoreReviewModalProps {
  reviewPublicId: string | null
  isOpen: boolean
  onClose: () => void
  onChanged?: () => void
}

const renderStars = (rating: number) => '★'.repeat(rating) + '☆'.repeat(Math.max(0, 5 - rating))

const AppStoreReviewModal = ({ reviewPublicId, isOpen, onClose, onChanged }: AppStoreReviewModalProps) => {
  const [review, setReview] = useState<AppStoreReviewDto | null>(null)
  const [draft, setDraft] = useState('')
  const [isSending, setIsSending] = useState(false)

  const loadReview = useCallback(async () => {
    if (!reviewPublicId) return
    try {
      const data = await adminService.getAppStoreReview(reviewPublicId)
      setReview(data)
      setDraft(data.responseBody ?? '')
    } catch (err) {
      console.log('Failed to load App Store review', err)
    }
  }, [reviewPublicId])

  useEffect(() => {
    if (isOpen && reviewPublicId) {
      loadReview()
    } else {
      setReview(null)
      setDraft('')
    }
  }, [isOpen, reviewPublicId, loadReview])

  const handleSend = async () => {
    if (!reviewPublicId || !draft.trim() || isSending) return
    setIsSending(true)
    try {
      const updated = await adminService.respondToAppStoreReview(reviewPublicId, draft.trim())
      setReview(updated)
      onChanged?.()
    } catch (err) {
      console.log('Failed to send App Store review response', err)
    } finally {
      setIsSending(false)
    }
  }

  return (
    <Modal
      closeTimeoutMS={300}
      isOpen={isOpen}
      onRequestClose={onClose}
      className="conversation-detail-popup feedback-thread-popup"
      overlayClassName="conversation-detail-overlay"
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
    >
      {review ? (
        <div className="popup-detail-container">
          <div className="feedback-thread-header">
            <div>
              <h2 title={review.title ?? undefined}>{review.title || 'App Store review'}</h2>
              <div className="feedback-thread-meta">
                <span>{renderStars(review.rating)}</span>
                <span>{review.reviewerNickname}</span>
                <span>{review.territory}</span>
              </div>
            </div>
          </div>

          <div className="popup-messages-section">
            <div className="feedback-original-post">
              <div className="feedback-original-label">Review</div>
              <div className="feedback-original-body">{review.body}</div>
              <div className="feedback-original-time">{dayjs(review.appleCreatedAt).format('MMM DD, YYYY [at] hh:mma')}</div>
            </div>

            {review.responseBody && (
              <div className="feedback-messages">
                <div className="feedback-message is-admin">
                  <div className="feedback-message-body">
                    <div className="feedback-message-meta">
                      {review.respondedByName && <span className="feedback-message-author">{review.respondedByName}</span>}
                      {review.respondedAt && <span>{dayjs(review.respondedAt).format('MMM DD, YYYY [at] hh:mma')}</span>}
                    </div>
                    <div className="feedback-message-bubble">
                      <div className="feedback-message-content">{review.responseBody}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="feedback-composer">
            <textarea
              value={draft}
              onChange={event => setDraft(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault()
                  handleSend()
                }
              }}
              placeholder="Write a response…"
              rows={3}
            />
            <div className="feedback-composer-footer">
              <span className="feedback-composer-hint">⌘/Ctrl + Enter to send</span>
              <button
                type="button"
                className="styled-button"
                onClick={handleSend}
                disabled={isSending || !draft.trim()}
              >
                {isSending ? 'Sending…' : review.isResponded ? 'Update response' : 'Send response'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div />
      )}
    </Modal>
  )
}

export default AppStoreReviewModal
